import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import CommonFields from "../../common/CommonField";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  TextArea,
} from "@carbon/react";

const AdditionalInfo = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 5,
  lastSavedStep = 0,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {
  // -----------------------------
  // DEBUG: Log received props
  // -----------------------------
  console.log('🔍 AdditionalInfo (Step 5) received:', {
    formMode,
    currentStep,
    lastSavedStep,
    isEditForm: formMode === "edit" || lastSavedStep >= currentStep,
    hasPropertyUuid: !!propertyUuid,
    hasData: !!data && Object.keys(data).length > 0,
    dataStructure: data,
    attributes: data?.attributes,
    additional_information: data?.additional_information,
    hasAdditionalInfo: !!data?.additional_information
  });

  // -----------------------------
  // CHILD EDIT MODE CALCULATION
  // -----------------------------
  const isEditForm = useMemo(() => {
    return formMode === "edit" || lastSavedStep >= currentStep;
  }, [formMode, lastSavedStep, currentStep]);

  // -----------------------------
  // FIELD MAP - Consistent pattern
  // -----------------------------
  const FIELD_MAP = useMemo(() => ({
    propertyDescription: "com_property_description",
    previousOccupancy: "com_previous_occupancy",
    propertyCleaning: "com_property_cleaning",
    propertyPainting: "com_property_painting"
  }), []);

  // -----------------------------
  // STATE
  // -----------------------------
  const [form, setForm] = useState({
    propertyDescription: "",
    previousOccupancy: "",
    propertyCleaning: "",
    propertyPainting: ""
  });

  const [, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // -----------------------------
  // API OPTIONS ONLY
  // -----------------------------
  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_MAP[fieldKey];
      if (!apiKey) return [];

      return attributeValues
        .filter((a) => a.attrName === apiKey)
        .map((a) => ({
          id: a.id,
          label: a.attr_val_name || a.label || a.value,
        }));
    },
    [attributeValues, FIELD_MAP]
  );

  // -----------------------------
  // FIELD UPDATES
  // -----------------------------
  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  useEffect(() => {
    if (!data || Object.keys(data).length === 0) {
      return;
    }
    const attributes = data.attributes || {};
    const additionalInfo = data.additional_information || {};
    const incoming = {};

   Object.entries(FIELD_MAP).forEach(([field, apiKey]) => {
      let raw;
      raw = additionalInfo[apiKey];
      if (raw === undefined || raw === null) {
        raw = attributes[apiKey];
      }
      if (raw == null) return;
      incoming[field] = String(raw);
    });
    
    if (Object.keys(incoming).length > 0) {
      setForm((prev) => {
        const newForm = { ...prev, ...incoming };
        return newForm;
      });
    } else {
    }
  }, [data, FIELD_MAP]);

  
  // SAVE - Consistent pattern
  
  const handleSave = useCallback(async (navigateNext = false) => {
    console.log('💾 AdditionalInfo handleSave:', { 
      navigateNext, 
      isEditForm, 
      currentStep,
      hasPropertyUuid: !!propertyUuid,
      formData: form 
    });

    setTouched(Object.keys(form).reduce((acc, f) => ({ ...acc, [f]: true }), {}));

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      let uuid = propertyUuid;

      if (!uuid && createProperty) {
        console.log('🆕 Creating property from Step 5...');
        uuid = await createProperty();
        onSaveSuccess?.(uuid, form, currentStep);
      }

      const payload = {};

      for (const [field, apiKey] of Object.entries(FIELD_MAP)) {
        const v = form[field];
        if (v == null || v === "") continue;

        // Convert dropdown values to numbers, keep text as string
        if (field === "previousOccupancy" || field === "propertyCleaning" || field === "propertyPainting") {
          payload[apiKey] = Number(v);
        } else {
          payload[apiKey] = v;
        }
      }

      console.log('💾 Saving additional info:', payload);
      await saveSectionAttributes(uuid, payload, form, "additional_information");

      setSuccess("Additional information saved successfully!");
      
      // Call parent's onSaveSuccess with step number
      onSaveSuccess?.(uuid, form, currentStep);
      
      // Call onComplete if provided
      onComplete?.(form);

      // Navigate to next step if requested
      if (navigateNext) {
        console.log('➡️ Navigating to next step after save');
        onNavigateNext?.(form);
      }
    } catch (err) {
      console.error('❌ Save failed:', err);
      setError(err.message || "Failed to save additional information");
    } finally {
      setSaving(false);
    }
  }, [
    form,
    isEditForm,
    currentStep,
    propertyUuid,
    createProperty,
    onSaveSuccess,
    FIELD_MAP,
    saveSectionAttributes,
    onComplete,
    onNavigateNext
  ]);

  // BUTTON HANDLERS
  
  const handleSaveOnly = useCallback((e) => {
    e?.preventDefault();
    handleSave(false);
  }, [handleSave]);

  const handleSaveAndNext = useCallback((e) => {
 
    handleSave(true);
  }, [handleSave]);

  // RENDER UI
  const { renderDropdown } = CommonFields({
    form,
    touched: {},
    getFieldError: () => null, 
    handleBlur: () => {},
    updateField,
    getOptions,
  });

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          timeout={4000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <Grid>
        <Column lg={16}>
          <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
            <Stack gap={6}>
              {/* Debug info - optional */}
              <div style={{ 
                backgroundColor: '#f4f4f4', 
                padding: '0.5rem', 
                borderRadius: '4px',
                fontSize: '0.75rem',
                color: '#666',
                display: 'none' // Hide in production
              }}>
                Debug: Form state - {JSON.stringify(form)}
              </div>

              {/* PROPERTY DESCRIPTION */}
              <FormGroup>
                <Grid>
                  <Column lg={16}>
                    <TextArea
                      id="propertyDescription"
                      labelText="Property Description"
                      value={form.propertyDescription || ""}
                      onChange={(e) => updateField("propertyDescription", e.target.value)}
                      placeholder="Provide any specific description you want to add about your property like furnishing and other amenities..."
                      rows={4}
                    />
                  </Column>
                </Grid>
              </FormGroup>

              {/* PREVIOUS OCCUPANCY & CLEANING SERVICE */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("previousOccupancy", "Previous Occupancy", false)}
                  </Column>
                  <Column lg={8}>
                    {renderDropdown("propertyCleaning", "I want to get my property cleaned", false)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* PAINTING SERVICE */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("propertyPainting", "I want to get my property painted", false)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* BUTTONS */}
              {CommonButtons && (
                <CommonButtons
                  onSave={handleSaveOnly}
                  onNext={handleSaveAndNext}
                  showSave
                  showNext
                  isSaveLoading={saving}
                  stepData={form}
                />
              )}
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default AdditionalInfo;